import { Button, Field, Modal, Stack, TextInput, Text, useNotify, tokens } from '@hotjar/hotsauce';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';
import type { PublicApiKey } from '../../domains/public-api';
import { deletePublicApiKey } from '../../domains/public-api/api';

type Props = { deleteKey: PublicApiKey | null; isOpen: boolean; onClose: () => void };

const CONFIRM_DELETE = 'DELETE';

interface KeyDeleteForm {
  displayName: string;
}

const StyledForm = styled.form`
  .error-message {
    margin-top: ${tokens.spacer.x1pt5};
  }
`;

export const DeleteApiKeyModal = ({ deleteKey, isOpen, onClose }: Props): JSX.Element => {
  const { t } = useTranslation('auth');
  const { showToast } = useNotify();
  const { register, handleSubmit, errors } = useForm<KeyDeleteForm>();

  const handleDeleteKey = async () => {
    if (!deleteKey) return;
    try {
      await deletePublicApiKey(deleteKey.organizationId, deleteKey.keyId);
      showToast({
        text: t('public-api.delete-key-modal.success-toast'),
        kind: 'success',
      });
    } catch (error) {
      showToast({
        text: t('public-api.delete-key-modal.error-toast'),
        kind: 'danger',
      });
    } finally {
      onClose();
    }
  };

  return (
    <Modal size="s" contentLabel="delete key" isOpen={isOpen} hideCloseButton>
      <StyledForm onSubmit={handleSubmit(handleDeleteKey)}>
        <Modal.Header
          title={t('public-api.delete-key-modal.title', { displayName: deleteKey?.displayName })}
        />
        <Modal.Body>
          <Stack preset="form">
            <Text as="p">
              {t('public-api.delete-key-modal.info', { displayName: CONFIRM_DELETE })}
            </Text>
            <Field
              labelText=""
              inputId="displayName"
              isInvalid={!!errors}
              invalidText={errors?.displayName?.message}
            >
              <TextInput
                placeholder={CONFIRM_DELETE}
                id="displayName"
                name="displayName"
                ref={register({ validate: (value) => value === CONFIRM_DELETE })}
                isInvalid={!!errors.displayName}
              />
              {errors.displayName && (
                <Text className="error-message" as="p">
                  {t('public-api.delete-key-modal.error', { displayName: CONFIRM_DELETE })}
                </Text>
              )}
            </Field>
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button kind="ghost" onClick={onClose}>
            {t('public-api.delete-key-modal.cancel-button')}
          </Button>
          <Button kind="danger" type="submit">
            {t('public-api.delete-key-modal.delete-button')}
          </Button>
        </Modal.Footer>
      </StyledForm>
    </Modal>
  );
};
